import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from '@emotion/styled';
import {
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  useMediaQuery,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  Autocomplete,
  Avatar,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import BedIcon from '@mui/icons-material/Bed';
import HotelIcon from '@mui/icons-material/Hotel';
import DateRangeIcon from '@mui/icons-material/DateRange';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import HelpIcon from '@mui/icons-material/Help';
import useCustomToast from '../hooks/useCustomToast';
import { getCurrencySymbolFromCode } from '../utils/currencyUtils';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

// Create a theme with orange as primary color to match the app's branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF7700',
      light: '#FF9D4D',
      dark: '#E56900',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#2E4057',
      light: '#4D6282',
      dark: '#1D2B3A',
      contrastText: '#FFF',
    },
    error: {
      main: '#FF5252',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h3: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 8px rgba(0, 0, 0, 0.05)',
    '0px 4px 16px rgba(0, 0, 0, 0.08)',
    '0px 6px 20px rgba(0, 0, 0, 0.1)',
    '0px 8px 24px rgba(0, 0, 0, 0.12)',
    '0px 10px 28px rgba(0, 0, 0, 0.14)',
    // ... rest of shadows
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.08)',
          overflow: 'visible',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '20px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0 20px 20px 20px',
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: 16,
        },
      },
    },
  },
});

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '32px 16px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px 12px',
  },
}));

const Header = styled(Box)(({ theme }) => ({
  marginBottom: 40,
  textAlign: 'center',
  position: 'relative',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    width: '60%',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    bottom: '-8px',
    left: '20%',
    borderRadius: '2px',
  },
}));

// Update RoomCard styled component for better design
const RoomCard = styled(motion.div)(({ theme, highlight }) => ({
  marginBottom: theme.spacing(3),
  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border 0.3s',
  position: 'relative',
  overflow: 'visible',
  border: highlight ? `3px solid ${theme.palette.primary.main}` : '2px solid #f0f0f0',
  background: highlight ? 'rgba(255, 119, 0, 0.08)' : '#fff',
  borderRadius: 20,
  boxShadow: highlight
    ? '0px 12px 32px rgba(255, 119, 0, 0.15)'
    : '0px 6px 16px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    transform: 'translateY(-5px) scale(1.01)',
    boxShadow: '0px 16px 32px rgba(255, 119, 0, 0.18)',
    border: `3px solid ${theme.palette.primary.main}`,
    background: 'rgba(255, 119, 0, 0.10)',
  },
}));

const RoomTypeIndicator = styled(Box)(({ theme, roomtype }) => {
  const getColor = () => {
    switch(roomtype) {
      case 1: return '#4CAF50'; // Standard - Green
      case 2: return '#FF7700'; // Deluxe - Orange
      case 3: return '#2196F3'; // Super Deluxe - Blue
      case 4: return '#9C27B0'; // Suite - Purple
      default: return '#757575'; // Default - Grey
    }
  };

  return {
    position: 'absolute',
    top: '-8px',
    left: '24px',
    padding: '4px 12px',
    borderRadius: '20px',
    backgroundColor: getColor(),
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    zIndex: 10,
  };
});

const AddRoomButton = styled(Button)(({ theme, roomtype }) => {
  const getColor = () => {
    switch(roomtype) {
      case 1: return '#4CAF50'; // Standard - Green
      case 2: return '#FF7700'; // Deluxe - Orange
      case 3: return '#2196F3'; // Super Deluxe - Blue
      case 4: return '#9C27B0'; // Suite - Purple
      default: return '#757575'; // Default - Grey
    }
  };

  return {
    backgroundColor: getColor(),
    color: 'white',
    margin: theme.spacing(1),
    '&:hover': {
      backgroundColor: getColor(),
      opacity: 0.9,
    }
  };
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

// Replace the thumbnail <motion.div> with a styled Card/Chip for better design
const ThumbnailCard = styled(Card)(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 80,
  maxWidth: 120,
  padding: theme.spacing(1, 1),
  margin: theme.spacing(0, 0.5),
  background: selected ? theme.palette.primary.light : '#fff',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #eee',
  boxShadow: selected ? '0 4px 16px rgba(255,119,0,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    background: theme.palette.primary.main,
    color: '#fff',
    boxShadow: '0 6px 20px rgba(255,119,0,0.18)',
  },
}));

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { 
      type: "spring", 
      stiffness: 100,
      damping: 15
    }
  }
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const SetInventory = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    hotel_id: id || '', // Initialize with URL param
    start_date: '',
    end_date: '',
  });
  const [hotelName, setHotelName] = useState('');
  const [roomDetails, setRoomDetails] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [allowedHotelIds, setAllowedHotelIds] = useState([]);
  const [occupancyByRoomType, setOccupancyByRoomType] = useState({}); // New state for occupancy
  const [hotelCurrency, setHotelCurrency] = useState('INR');
  
  const { successToast, errorToast, infoToast } = useCustomToast();
  
  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Add refs for each room card
  const roomRefs = useRef([]);

  // State for highlighting the scrolled-to room
  const [highlightedIndex, setHighlightedIndex] = useState(null);

  // Function to fetch hotel details and occupancy
  const fetchHotelDetails = async (hotelId) => {
    if (!hotelId) return;
    
    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast("Please login to continue");
        return;
      }

      const formData = new FormData();
      formData.append('hotel_id', hotelId);
      formData.append('token', token);

      const response = await fetch('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.status && data.amenities_by_category) {
        const occupancyMap = {};
        data.amenities_by_category.forEach(category => {
          occupancyMap[category.category_id] = category.occupancy || 1;
        });
        setOccupancyByRoomType(occupancyMap);
        if (data.hotel_details?.currency) {
          setHotelCurrency(data.hotel_details.currency);
        }
        console.log('Occupancy map fetched:', occupancyMap);
      } else {
        console.warn('No amenities_by_category found in API response:', data);
      }
    } catch (error) {
      console.error('Error fetching hotel details:', error);
      errorToast('Failed to fetch room type occupancy information');
    }
  };

  // Update useEffect to use URL param and fetch hotel details
  useEffect(() => {
    // First check URL param from useParams
    if (id) {
      setFormData((prev) => ({ ...prev, hotel_id: id }));
      fetchHotelDetails(id);
    } else {
      // Fallback to localStorage only if no URL param
      const hotelIdFromStorage = localStorage.getItem('selectedHotelId');
      if (hotelIdFromStorage) {
        setFormData((prev) => ({ ...prev, hotel_id: hotelIdFromStorage }));
        fetchHotelDetails(hotelIdFromStorage);
      }
    }
    
    // Set hotel name if available
    const hotelNameFromStorage = localStorage.getItem('selectedHotelName');
    if (hotelNameFromStorage) {
      setHotelName(hotelNameFromStorage);
    }
  }, [id]);

  // Search room types when user types
  const searchRoomTypes = async (query) => {
    if (!formData.hotel_id || !query || query.length < 2) {
      setRoomTypeOptions([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAvailableRoomTypes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          hotel_id: formData.hotel_id,
          search: query // Add search parameter if API supports it
        })
      });
      
      const data = await response.json();
      if (data.status && Array.isArray(data.data)) {
        // Filter results locally as well for better performance
        const filtered = data.data.filter(option => 
          (option.safe_name || option.name || '').toLowerCase().includes(query.toLowerCase())
        ).slice(0, 50); // Limit to 50 results
        setRoomTypeOptions(filtered);
      } else {
        setRoomTypeOptions([]);
      }
    } catch (error) {
      console.error('Error searching room types:', error);
      setRoomTypeOptions([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchRoomTypes(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, formData.hotel_id]);

  // Always log hotelList on mount
  useEffect(() => {
    try {
      const encrypted = localStorage.getItem('hotelsList');
      if (!encrypted) {
        console.warn('No hotelList found in localStorage.');
        return;
      }
      const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      if (!decrypted) {
        console.error('Decryption failed: decrypted string is empty.');
        return;
      }
      let hotelList;
      try {
        hotelList = JSON.parse(decrypted);
      } catch (parseErr) {
        console.error('Failed to parse decrypted hotelList:', parseErr, decrypted);
        return;
      }
      if (!hotelList || !Array.isArray(hotelList) || hotelList.length === 0) {
        console.warn('Decrypted hotelList is empty or not an array:', hotelList);
      } else {
        console.log('Decrypted hotelList from localStorage:', hotelList);
      }
    } catch (e) {
      console.error('Failed to decrypt or parse hotelList from localStorage:', e);
    }
  }, []);

  // Refactor addRoomDetail to store the full room type object
  const addRoomDetail = (roomtype_id) => {
    if (!selectedRoomTypes.has(roomtype_id)) {
      const roomTypeObj = roomTypeOptions.find(rt => rt.id === roomtype_id || rt.id === parseInt(roomtype_id));
      if (!roomTypeObj) return;
      
      // Get occupancy from API response, default to 1 if not found
      // This occupancy value determines how many guest rate input boxes will be shown
      const occupancy = occupancyByRoomType[roomtype_id] || 1;
      
      // Create initial rates object based on occupancy from API
      const initialRates = {};
      for (let i = 1; i <= occupancy; i++) {
        initialRates[`${i}guest`] = 1000 + ((i - 1) * 500); // Default rates with increment
      }
      
      setSelectedRoomTypes((prev) => new Set([...prev, roomtype_id]));
      setRoomDetails([
        ...roomDetails,
        {
          roomtype: roomTypeObj,
          roomtype_id: parseInt(roomtype_id),
          safe_name: roomTypeObj.safe_name,
          rate: initialRates,
          count: 1,
          occupancy: occupancy, // Use occupancy from API
        },
      ]);
    }
  };

  // Remove a room
  const removeRoomDetail = (index, roomtype_id) => {
    const updatedRoomDetails = [...roomDetails];
    updatedRoomDetails.splice(index, 1);
    setRoomDetails(updatedRoomDetails);
    
    const updatedSelectedRoomTypes = new Set(selectedRoomTypes);
    updatedSelectedRoomTypes.delete(roomtype_id);
    setSelectedRoomTypes(updatedSelectedRoomTypes);
  };

  // Update room details dynamically
  const updateRoomDetail = (index, field, value) => {
    const updatedRoomDetails = [...roomDetails];
    if (field === 'rate') {
      // If updating rate, merge with existing rates instead of replacing
      updatedRoomDetails[index].rate = { 
        ...updatedRoomDetails[index].rate, 
        ...value 
      };
    } else {
      // Ensure numeric values are not null
      if (typeof value === 'number' && isNaN(value)) {
        value = 0;
      }
      updatedRoomDetails[index][field] = value || '';
    }
    setRoomDetails(updatedRoomDetails);
  };

  // Validate form data before submission
  const validateFormData = () => {
    const { hotel_id, start_date, end_date } = formData;

    // Check required fields
    if (!hotel_id || !start_date || !end_date) {
      setError('Hotel ID, Start Date, and End Date are required.');
      return false;
    }

    // Validate date range
    const startDateObj = new Date(start_date);
    const endDateObj = new Date(end_date);
    if (startDateObj > endDateObj) {
      setError('Start Date cannot be later than End Date.');
      return false;
    }

    // Validate room details
    if (roomDetails.length === 0) {
      setError('At least one room type must be added.');
      return false;
    }

    for (const room of roomDetails) {
      // Validate room type ID
      if (!room.roomtype_id || isNaN(parseInt(room.roomtype_id))) {
        setError('Invalid room type ID.');
        return false;
      }

      // Validate room count
      const count = parseInt(room.count);
      if (isNaN(count) || count < 0) {
        setError('Room count must be a non-negative number.');
        return false;
      }

      // Validate rates
      if (!room.rate || Object.keys(room.rate).length === 0) {
        setError('Each room type must have at least one rate defined.');
        return false;
      }

      // Validate each rate value
      for (const [key, value] of Object.entries(room.rate)) {
        if (!key.match(/^\d+guest$/)) {
          setError('Invalid rate key format. Expected format: "1guest", "2guest", etc.');
          return false;
        }
        if (isNaN(parseInt(value)) || parseInt(value) < 0) {
          setError('Rate values must be non-negative numbers.');
          return false;
        }
      }
    }

    return true;
  };

  // Handle form submission and send data to the APIs
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!validateFormData()) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = getDecryptedToken();
      
      // Format data for setBulkRates API
      const bulkRatesData = {
        hotel_id: formData.hotel_id,
        token: token,
        start_date: formData.start_date,
        end_date: formData.end_date,
        room_types: roomDetails.map(room => {
          // Ensure rate object has proper guest keys
          const formattedRate = {};
          Object.entries(room.rate).forEach(([key, value]) => {
            // Convert key to proper format (e.g., "1guest", "2guest", etc.)
            const guestNum = key.replace(/[^0-9]/g, '');
            formattedRate[`${guestNum}guest`] = parseInt(value);
          });
          
          return {
            roomtype_id: room.roomtype_id.toString(),
            rate: JSON.stringify(formattedRate),
            occupancy: Object.keys(room.rate).length // Number of guest rates defines occupancy
          };
        })
      };
      
      // Format data for setCount API
      const setCountData = {
        hotel_id: parseInt(formData.hotel_id, 10),
        token: token,
        start_date: formData.start_date,
        end_date: formData.end_date,
        room_types: roomDetails.map(room => ({
          roomtype_id: room.roomtype_id.toString(),
          count: parseInt(room.count) || 0 // Ensure count is a number and not null
        }))
      };
      
      console.log("Submitting to setCount API:", setCountData);
      
      // First call setCount API
      const setCountResponse = await fetch('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setCount', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          'Accept': 'application/json'
          },
        body: JSON.stringify(setCountData),
      });
      
      const setCountResult = await setCountResponse.json();
      console.log("setCount API response:", setCountResult);

      if (!setCountResult.status) {
        throw new Error(`Inventory Error: ${setCountResult.message || 'Failed to save inventory'}`);
      }

      // Show success message for setCount
      successToast("Room inventory updated successfully!");

      // Wait for 4 seconds before calling setBulkRates
      await new Promise(resolve => setTimeout(resolve, 4000));

      console.log("Submitting to setBulkRates API:", bulkRatesData);
      
      // Then call setBulkRates API
      const bulkRatesResponse = await fetch('https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setBulkRates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        body: JSON.stringify(bulkRatesData),
      });
      
      const bulkRatesResult = await bulkRatesResponse.json();
      console.log("setBulkRates API response:", bulkRatesResult);

        if (!bulkRatesResult.status) {
        throw new Error(`Rates Error: ${bulkRatesResult.message || 'Failed to save rates'}`);
        }

      // Show final success message
      setSuccess("Room rates and inventory data saved successfully!");
      successToast("Room rates and inventory data saved successfully!");
      
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = error.message || "An error occurred while saving the data";
      setError(errorMessage);
      errorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to map room type IDs to names
  const getRoomTypeName = (roomtype_id) => {
    const roomTypeObj = roomTypeOptions.find(rt => rt.id === roomtype_id || rt.id === parseInt(roomtype_id));
    return roomTypeObj ? roomTypeObj.safe_name : 'Unknown Room Type';
  };

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Header>
              <SectionTitle variant="h3">
                Inventory & Rate Manager
              </SectionTitle>
              {hotelName && (
                <Chip
                  label={`${hotelName} (ID: ${formData.hotel_id})`}
                  color="primary"
                  icon={<HotelIcon />}
                  sx={{ 
                    mb: 2, 
              fontSize: '1rem',
                    padding: '18px 15px',
                    '& .MuiChip-label': { fontWeight: 500 }
                  }}
                />
              )}
              <Chip
                label={`Currency: ${getCurrencySymbolFromCode(hotelCurrency)}`}
                color="success"
                variant="outlined"
                sx={{ mb: 2, fontWeight: 600 }}
              />
              <Typography variant="body1" color="textSecondary">
                Set room availability and rates for a specific date range
              </Typography>
            </Header>
          </motion.div>

          <Paper 
            elevation={2} 
            sx={{ 
              padding: { xs: 2, sm: 3, md: 4 },
              borderRadius: '16px',
              mb: 4 
            }}
            component={motion.div}
            variants={itemVariants}
          >
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Hotel ID */}
                <Grid item xs={12} md={4}>
                  <StyledTextField
                    fullWidth
                    label="Hotel ID"
                    variant="outlined"
                    value={formData.hotel_id}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*$/.test(inputValue)) {
                        if (!id && allowedHotelIds.length > 0 && inputValue && !allowedHotelIds.includes(inputValue)) {
                          errorToast('You don’t have access to this property id');
                          return;
                        }
                        setFormData({ ...formData, hotel_id: inputValue });
                      }
                    }}
                    required
                    error={Boolean(
                      !id &&
                        allowedHotelIds.length > 0 &&
                        formData.hotel_id &&
                        !allowedHotelIds.includes(formData.hotel_id)
                    )}
                    helperText={
                      !id &&
                      allowedHotelIds.length > 0 &&
                      formData.hotel_id &&
                      !allowedHotelIds.includes(formData.hotel_id)
                        ? "You don’t have access to this property id"
                        : "Enter a valid hotel ID from your list."
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HotelIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

        {/* Date Range */}
                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    fullWidth
                    label="Start Date"
              type="date"
                    variant="outlined"
              value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DateRangeIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <StyledTextField
                    fullWidth
                    label="End Date"
              type="date"
                    variant="outlined"
              value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DateRangeIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Room Types Section */}
              <Box sx={{ mt: 5, mb: 3 }}>
                <SectionTitle variant="h4">
                  Add Room Types
                </SectionTitle>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 3 }}>
                  Add the room types you want to configure rates and availability for
                </Typography>

                <Box sx={{ maxWidth: 400, mx: 'auto', mb: 4 }}>
                  <Autocomplete
                    options={roomTypeOptions.filter(rt => !selectedRoomTypes.has(rt.id))}
                    getOptionLabel={option => option.safe_name || option.name || ''}
                    onChange={(event, newValue) => {
                      if (!formData.hotel_id) {
                        errorToast('Please enter/select a valid hotel ID first.');
                        return;
                      }
                      if (newValue && !selectedRoomTypes.has(newValue.id)) {
                        addRoomDetail(newValue.id);
                        setSearchQuery(''); // Clear search after selection
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        setSearchQuery(newInputValue);
                      }
                    }}
                    inputValue={searchQuery}
                    renderInput={params => (
                      <StyledTextField 
                        {...params} 
                        label="Add Room Type" 
                        variant="outlined" 
                        helperText={!formData.hotel_id ? 'Enter/select a hotel ID first.' : 'Type to search room types (min 2 characters).'} 
                        placeholder="Start typing to search..."
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                    filterSelectedOptions
                    clearOnBlur={false}
                    disabled={!formData.hotel_id}
                    noOptionsText={searchQuery.length < 2 ? "Type at least 2 characters to search" : "No room types found"}
                    loading={isSearching}
                    open={searchQuery.length >= 2 && roomTypeOptions.length > 0}
                    onOpen={() => {}}
                    onClose={() => {}}
                  />
                </Box>
              </Box>

              {/* Room Details */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, overflowX: 'auto', py: 1 }}>
                {roomDetails.map((room, idx) => (
                  <motion.div
                    key={room.roomtype_id}
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.97 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setHighlightedIndex(idx);
                      if (roomRefs.current[idx]) {
                        roomRefs.current[idx].scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }}
                  >
                    <ThumbnailCard selected={highlightedIndex === idx}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, mb: 0.5 }}>
                        <BedIcon />
                      </Avatar>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          textAlign: 'center',
                          maxWidth: 70,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: highlightedIndex === idx ? '#fff' : 'inherit',
                        }}
                        title={room.safe_name}
                      >
                        {room.safe_name}
                      </Typography>
                    </ThumbnailCard>
                  </motion.div>
                ))}
              </Box>
              <AnimatePresence>
                {roomDetails.map((room, index) => (
                  <motion.div
                    key={`${room.roomtype_id}-${index}`}
                    ref={el => roomRefs.current[index] = el}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30, mass: 1 }}
                    onAnimationComplete={() => {
                      if (highlightedIndex === index) {
                        setTimeout(() => setHighlightedIndex(null), 1200);
                      }
                    }}
                  >
                    <RoomCard highlight={highlightedIndex === index ? true : undefined}>
                      <RoomTypeIndicator roomtype={room.roomtype_id}>
                        {room.safe_name}
                      </RoomTypeIndicator>
                      
                      <CardHeader
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BedIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="h6">
                              Room Configuration
                            </Typography>
                          </Box>
                        }
                        action={
                          <Tooltip title="Remove Room">
                            <IconButton 
                              color="error" 
                              onClick={() => removeRoomDetail(index, room.roomtype_id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        }
                      />
                      
                      <CardContent>
                        <Grid container spacing={3}>
                          {/* Number of Guests */}
                          <Grid item xs={12} sm={6} md={4}>
                            <StyledTextField
                              fullWidth
                              label="Number of Guests Allowed"
                              type="number"
                              variant="outlined"
                              value={room.occupancy}
                              onChange={(e) => {
                                const newOccupancy = parseInt(e.target.value) || 1;
                                if (newOccupancy >= 1 && newOccupancy <= 10) {
                                  const currentOccupancy = room.occupancy;
                                  
                                  // If reducing occupancy, warn about losing rates
                                  if (newOccupancy < currentOccupancy) {
                                    const willLoseRates = currentOccupancy - newOccupancy;
                                    if (willLoseRates > 0) {
                                      if (!window.confirm(`Reducing guests from ${currentOccupancy} to ${newOccupancy} will remove ${willLoseRates} guest rate field(s). Continue?`)) {
                                        return; // User cancelled
                                      }
                                    }
                                  }
                                  
                                  // Update occupancy
                                  updateRoomDetail(index, 'occupancy', newOccupancy);
                                  
                                  // Update rates object to match new occupancy
                                  const newRates = {};
                                  for (let i = 1; i <= newOccupancy; i++) {
                                    const guestKey = `${i}guest`;
                                    newRates[guestKey] = room.rate[guestKey] || 0;
                                  }
                                  updateRoomDetail(index, 'rate', newRates);
                                  
                                  // Show success message
                                  successToast(`Updated to ${newOccupancy} guest${newOccupancy > 1 ? 's' : ''} allowed`);
                                }
                              }}
                              InputProps={{
                                inputProps: { 
                                  min: 1, 
                                  max: 10,
                                  style: { textAlign: 'center' }
                                },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                              helperText="Change to update guest rate fields (1-10 guests) - Editable field"
                            />
                          </Grid>
                          
                          {/* Room Count */}
                          <Grid item xs={12} sm={6} md={4}>
                            <StyledTextField
                              fullWidth
                              label="Number of Rooms Available"
                              type="number"
                              variant="outlined"
                              value={room.count}
                              onChange={(e) => updateRoomDetail(index, 'count', parseInt(e.target.value))}
                              required
                              InputProps={{
                                inputProps: { min: 0 },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <BedIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                        
                {/* Guest Rates */}
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                            Guest Rates ({getCurrencySymbolFromCode(hotelCurrency)})
                          </Typography>
                          
                          <AnimatePresence>
                            <Grid container spacing={2}>
                              {[...Array(room.occupancy).keys()].map((_, i) => {
                                const guestKey = `${i + 1}guest`;
                                return (
                                  <Grid item xs={12} sm={6} md={4} lg={3} key={guestKey}>
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0.9 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <StyledTextField
                                        fullWidth
                                        label={`${guestKey.replace('guest', ' Guest Rate')} (${getCurrencySymbolFromCode(hotelCurrency)})`}
                                        type="number"
                                        variant="outlined"
                                        value={room.rate[guestKey] || ''}
                                        onChange={(e) => {
                                          const newValue = parseFloat(e.target.value) || 0;
                                          updateRoomDetail(index, 'rate', {
                                            [guestKey]: newValue,
                                          });
                                        }}
                                        required
                                        InputProps={{
                                          inputProps: { min: 0 },
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <Chip 
                                                icon={<PersonIcon fontSize="small" />} 
                                                label={guestKey.replace('guest', '')} 
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                              />
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </motion.div>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </AnimatePresence>
                        </Box>
                      </CardContent>
                    </RoomCard>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* No rooms message */}
              {roomDetails.length === 0 && (
                <Box 
                  sx={{ 
                    textAlign: 'center', 
                    py: 6, 
                    backgroundColor: 'rgba(0,0,0,0.02)', 
                    borderRadius: 2 
                  }}
                  component={motion.div}
                  variants={fadeInVariants}
                >
                  <BusinessCenterIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="subtitle1" color="textSecondary">
                    No room types added yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Please add at least one room type to continue
                  </Typography>
                </Box>
              )}

              {/* Error and Success Messages */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  </motion.div>
                )}

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Alert severity="success" sx={{ my: 2 }} onClose={() => setSuccess(null)}>
                      {success}
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

        {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{ 
                      minWidth: { xs: '100%', sm: '200px' },
                      py: 1.5
                    }}
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </motion.div>
              </Box>
      </form>
          </Paper>
        </motion.div>
      </PageContainer>
    </ThemeProvider>
  );
};

export default SetInventory;